import React, { useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from './utils/supabase';
import PrototypeTest from './components/PrototypeTest';
import AdminDashboard from './components/AdminDashboard';
import Auth from './components/Auth';
import defaultConfig from './data/defaultConfig';

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionProgress, setSessionProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const isAdminRoute = location.pathname === '/admin';

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setIsLoading(false);
      return;
    }

    supabase?.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase?.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setIsLoading(false);
    }) || { data: { subscription: { unsubscribe: () => {} } } };

    return () => subscription.unsubscribe();
  }, []);

  const handleProgressUpdate = (progress: number, stageName: string) => {
    setSessionProgress(progress);
    setCurrentStage(stageName);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isSupabaseConfigured()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Configuration Required</h2>
          <p className="text-gray-600 mb-4">
            Please connect your Supabase project to continue. Click the "Connect to Supabase" button in the top right corner.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <header className="bg-white shadow-sm">
        <div className="max-w-[90vw] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="w-20"></div>
            
            {!isAdminRoute && (
              <div className="flex-1 flex flex-col items-center">
                <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all duration-300 ease-in-out"
                    style={{ width: `${sessionProgress}%` }}
                  />
                </div>
                {currentStage && (
                  <span className="text-sm text-gray-600 mt-1">{currentStage}</span>
                )}
              </div>
            )}
            
            {session && (
              <button
                onClick={() => supabase?.auth.signOut()}
                className="w-20 text-right text-sm text-gray-600 hover:text-gray-900"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>
      </header>
      
      <main className="flex-grow max-w-[90vw] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route 
            path="/admin" 
            element={
              session ? (
                <AdminDashboard 
                  variants={defaultConfig.variants} 
                  questions={defaultConfig.questions}
                />
              ) : (
                <Auth />
              )
            } 
          />
          <Route 
            path="/" 
            element={
              <PrototypeTest 
                config={defaultConfig} 
                onReset={() => window.location.reload()}
                onProgressUpdate={handleProgressUpdate}
              />
            } 
          />
          <Route path="*" element={<Navigate to="/\" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;